// src/app/companies/components/company-import/company-import.component.ts
import { Component, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { lastValueFrom, take } from 'rxjs';
import { AppConfigTopNavComponent } from '../../../../shared/app-config-nav/app-config-top-nav.component';
import { CompanyFileParserService } from './services/company-file-parser.service';
import { CompanyUploadService, Company } from './services/company-upload.service';
import { CompanyImportRow } from './models/company-import-row.model';

// Reuse your existing models if they already live elsewhere.
// This matches the shapes your EmployeeImportComponent expects.
export interface AggregatedError {
  field: string;
  values: string[];
}

export interface UploadLog {
  uploadedBy: string;
  timestamp: Date;
  durationMs: number;
  totalRows: number;
  successCount: number;
  errors: { rowIndex: number; message: string }[];
  warnings: { rowIndex: number; message: string }[];
  summary: UploadRowSummary[];
  // Company-specific: created items list for the modal
  createdCompanies?: Company[];
}

export interface UploadRowSummary {
  rowIndex: number;
  employeeNumber: string; // unused for companies but kept for compatibility
  status: 'success' | 'partial' | 'fail';
  errors: string[];
  warnings: string[];
}

@Component({
  selector: 'app-company-import',
  standalone: true,
  imports: [CommonModule, AppConfigTopNavComponent],
  templateUrl: './company-import.component.html',
  styleUrls: ['./company-import.component.scss'],
})
export class CompanyImportComponent {
  selectedFile: File | null = null;

  logs: UploadLog[] = [];
  aggregatedErrors: AggregatedError[] = [];
  activeLog: UploadLog | null = null;

  isLoading = false;
  errorMessage: string | null = null;

  constructor(
    private readonly parser: CompanyFileParserService,
    private readonly uploadService: CompanyUploadService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.reset();
    this.selectedFile = input.files[0];
    input.value = '';
  }

  handleDrop(event: DragEvent) {
    event.preventDefault();
    if (!event.dataTransfer?.files?.length) return;
    this.reset();
    this.selectedFile = event.dataTransfer.files[0];
  }

  removeFile() {
    this.reset();
  }

  private reset() {
    this.selectedFile = null;
    this.errorMessage = null;
    this.aggregatedErrors = [];
    this.activeLog = null;
  }

  async upload() {
    if (!this.selectedFile) return;

    this.isLoading = true;
    this.errorMessage = null;

    const startTime = performance.now();
    this.cdr.detectChanges();

    try {
      // 1) Parse file (strict template)
      const rows: CompanyImportRow[] = await lastValueFrom(this.parser.parse(this.selectedFile).pipe(take(1)));
      const totalRows = rows.length;

      // Guard: empty after parsing (e.g., headers only)
      if (!totalRows) {
        throw new Error('No data rows found in file');
      }

      // 2) Upload (backend returns created companies; duplicates may yield [])
      const createdCompanies = await lastValueFrom(this.uploadService.uploadRows(rows).pipe(take(1)));
      const createdCount = createdCompanies.length;

      // 3) Current user
      const user = await lastValueFrom(this.uploadService.getCurrentUser().pipe(take(1)));
      const uploadedBy = `${user.lastName}, ${user.firstName} (${user.id})`;

      // 4) Build result message (since backend has no row-level errors)
      const resultValues: string[] = [];
      resultValues.push(`Imported ${createdCount}/${totalRows}.`);

      if (createdCount === 0) {
        resultValues.push('No records were created. This is commonly caused by duplicates.');
      } else if (createdCount < totalRows) {
        resultValues.push(
          `${totalRows - createdCount} row(s) were not imported (commonly duplicates).`
        );
      }

      this.aggregatedErrors = [
        {
          field: 'Upload Result',
          values: resultValues,
        },
      ];

      // 5) Log (keep summary empty; we don’t have row-level outcomes)
      const newLog: UploadLog = {
        uploadedBy,
        timestamp: new Date(),
        durationMs: Math.round(performance.now() - startTime),
        totalRows,
        successCount: createdCount,
        errors: [],
        warnings: [],
        summary: [],
        createdCompanies,
      };

      this.logs = [newLog, ...this.logs];
      this.activeLog = newLog;

      this.cdr.detectChanges();
    } catch (err: any) {
      this.errorMessage = err?.error?.message ?? err?.message ?? 'Upload failed';
      this.cdr.detectChanges();
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }
}